import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import {
  ASSET_CLASSES, CLASS_LABEL, investmentPlanService,
  type ReminderResult, type ReminderRule,
} from '../../services/investment-plan-service';

const EMPTY: ReminderRule = {
  name: '', enabled: true, kind: 'no_buys_in_window', scope: { institution: '', side: 'buy' },
  window_days: 21, repeat_every_days: 7, channel: 'email', message_template: '',
};

const STATUS: Record<string, { label: string; color: string }> = {
  fired: { label: 'Fired', color: 'var(--warn-text)' },
  suppressed: { label: 'Suppressed: no fresh data', color: 'var(--danger-text)' },
  ok: { label: 'OK', color: 'var(--inflow-text)' },
  throttled: { label: 'Reminded recently', color: 'var(--text-secondary)' },
  disabled: { label: 'Disabled', color: 'var(--text-secondary)' },
};

/**
 * Plan page "Reminders" (issue #33 follow-up): data-driven reminder rules.
 * A rule fires only on fresh data; when its broker is not syncing it is
 * "suppressed" and shown here and on the dashboard, never emailed.
 */
export const RemindersCard: React.FC<{ institutions: string[] }> = ({ institutions }) => {
  const [rules, setRules] = useState<ReminderRule[]>([]);
  const [kinds, setKinds] = useState<string[]>(['no_buys_in_window']);
  const [edit, setEdit] = useState<ReminderRule | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<Record<string, ReminderResult>>({});

  const load = useCallback(async () => {
    try {
      const r = await investmentPlanService.reminders();
      setRules(r.reminders ?? []);
      setKinds(r.kinds?.length ? r.kinds : ['no_buys_in_window']);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load reminders');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!edit) return;
    try {
      await investmentPlanService.saveReminder({
        ...edit,
        scope: { ...edit.scope, asset_class: edit.scope.asset_class || undefined,
          symbols: (edit.scope.symbols ?? []).filter(Boolean) },
      });
      setEdit(null);
      setError(null);
      await load();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save the reminder');
    }
  };

  const status = (r: ReminderRule) => {
    const t = r.id ? tested[r.id] : undefined;
    const s = t ? t.status : r.last_event?.status;
    const why = t ? t.reason : r.last_event?.reason;
    if (!s) return <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>Not evaluated yet</Typography>;
    const tone = STATUS[s] ?? STATUS.ok;
    return (
      <Typography variant="caption" sx={{ display: 'block' }}>
        <span style={{ color: tone.color, fontWeight: 600 }}>{t ? 'Test: ' : 'Last: '}{tone.label}</span>
        {why ? ` — ${why}` : ''}{!t && r.last_event ? ` (${r.last_event.fired_at.slice(0, 10)})` : ''}
      </Typography>
    );
  };

  return (
    <Card sx={{ mb: 3 }} component="section" aria-label="Reminders">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="h2">Reminders</Typography>
          <Button size="small" onClick={() => setEdit({ ...EMPTY, scope: { ...EMPTY.scope } })}>Add reminder</Button>
        </Stack>
        <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 1 }}>
          Checked every night. A reminder only fires when the broker's data is fresh.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {rules.length === 0 && !edit && <Typography variant="body2">No reminders yet.</Typography>}
        {rules.map((r) => (
          <Box key={r.id} sx={{ py: 1, borderTop: '1px solid var(--border-soft)' }} aria-label={`Reminder ${r.name}`}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
              <Typography sx={{ fontWeight: 650 }}>{r.name}</Typography>
              {!r.enabled && <Chip size="small" variant="outlined" label="off" />}
              <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                {r.kind.replace(/_/g, ' ')} · {r.scope.institution || `${r.scope.account_ids?.length ?? 0} account(s)`}
                {r.scope.symbols?.length ? ` · ${r.scope.symbols.join(', ')}` : ''}
                {r.scope.asset_class ? ` · ${r.scope.asset_class}` : ''} · {r.scope.side ?? 'buy'} · {r.window_days} days
                · repeats every {r.repeat_every_days} days · {r.channel}
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Button size="small" onClick={async () => {
                  const t = await investmentPlanService.testReminder(r.id!);
                  setTested((x) => ({ ...x, [r.id!]: t.result }));
                }}>Test</Button>
                <Button size="small" onClick={() => setEdit({ ...r, scope: { ...r.scope } })}>Edit</Button>
                <Button size="small" color="error" onClick={async () => { await investmentPlanService.deleteReminder(r.id!); await load(); }}>
                  Delete
                </Button>
              </Box>
            </Stack>
            {status(r)}
          </Box>
        ))}
        {edit && (
          <Box sx={{ mt: 2, p: 2, border: '1px solid var(--border-soft)', borderRadius: 2 }} aria-label="Reminder form">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Name" value={edit.name}
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" select label="Kind" value={edit.kind}
                  onChange={(e) => setEdit({ ...edit, kind: e.target.value })}>
                  {kinds.map((k) => <MenuItem key={k} value={k}>{k.replace(/_/g, ' ')}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth size="small" label="Broker / institution" value={edit.scope.institution ?? ''}
                  inputProps={{ list: 'reminder-institutions', 'aria-label': 'Broker' }}
                  onChange={(e) => setEdit({ ...edit, scope: { ...edit.scope, institution: e.target.value } })} />
                <datalist id="reminder-institutions">
                  {institutions.map((i) => <option key={i} value={i} />)}
                </datalist>
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" label="Symbols (optional)" value={(edit.scope.symbols ?? []).join(', ')}
                  onChange={(e) => setEdit({ ...edit, scope: { ...edit.scope, symbols: e.target.value.split(/[\s,]+/) } })} />
              </Grid>
              <Grid item xs={6} sm={4}>
                <TextField fullWidth size="small" select label="Asset class (optional)" value={edit.scope.asset_class ?? ''}
                  onChange={(e) => setEdit({ ...edit, scope: { ...edit.scope, asset_class: e.target.value as ReminderRule['scope']['asset_class'] } })}>
                  <MenuItem value="">Any</MenuItem>
                  {ASSET_CLASSES.map((c) => <MenuItem key={c} value={c}>{CLASS_LABEL[c]}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField fullWidth size="small" select label="Side" value={edit.scope.side ?? 'buy'}
                  onChange={(e) => setEdit({ ...edit, scope: { ...edit.scope, side: e.target.value as 'buy' | 'sell' } })}>
                  <MenuItem value="buy">Buy</MenuItem>
                  <MenuItem value="sell">Sell</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField fullWidth size="small" label="Window (days)" value={edit.window_days}
                  inputProps={{ 'aria-label': 'Window days', inputMode: 'numeric' }}
                  onChange={(e) => setEdit({ ...edit, window_days: parseInt(e.target.value, 10) || 0 })} />
              </Grid>
              <Grid item xs={4} sm={2}>
                <TextField fullWidth size="small" label="Repeat (days)" value={edit.repeat_every_days}
                  onChange={(e) => setEdit({ ...edit, repeat_every_days: parseInt(e.target.value, 10) || 0 })} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Message (optional: {days} {broker} {last_buy_date})"
                  value={edit.message_template ?? ''} onChange={(e) => setEdit({ ...edit, message_template: e.target.value })} />
              </Grid>
            </Grid>
            <Stack direction="row" gap={1} sx={{ mt: 1.5 }} alignItems="center">
              <FormControlLabel control={<Switch checked={edit.enabled} onChange={(e) => setEdit({ ...edit, enabled: e.target.checked })} />}
                label="Enabled" />
              <Button variant="contained" size="small" onClick={save}
                disabled={!edit.name.trim() || !(edit.scope.institution ?? '').trim() || edit.window_days < 1}>Save reminder</Button>
              <Button size="small" onClick={() => setEdit(null)}>Cancel</Button>
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
